import {
  AppBar, Box, Button, Container, Divider, Drawer, IconButton, List,
  ListItemButton, ListItemText, Stack, Toolbar, Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '@/auth/AuthProvider';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Public marketplace layout.
 *
 * Desktop: brand + horizontal nav + auth area in one toolbar.
 * Mobile (<sm): brand + hamburger; nav links collapse into a temporary
 * drawer. Sign-in button stays visible if anonymous, because that's the
 * primary CTA for unauthenticated visitors.
 */

const PUBLIC_LINKS = [
  { to: '/find-care',                label: 'Find care' },
  { to: '/specialties',              label: 'Specialties' },
  { to: '/transparency/fairness',    label: 'Transparency' },
];

export function PublicLayout() {
  const { status, redirectToLogin, user } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)}
                        sx={{ mr: 1.5 }} aria-label="open navigation">
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              color: 'primary.main', textDecoration: 'none', fontWeight: 700,
              mr: { xs: 1, md: 4 },
              flexShrink: 0,
              fontSize: { xs: '1rem', sm: '1.125rem' },
            }}
          >
            MyBooking Health
          </Typography>
          <Stack direction="row" spacing={3}
                 sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {PUBLIC_LINKS.map((l) => (
              <Button key={l.to} component={RouterLink} to={l.to} color="inherit">
                {l.label}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />
          {status === 'anonymous' ? (
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => redirectToLogin()}
            >
              Sign in
            </Button>
          ) : status === 'authenticated' ? (
            <Stack direction="row" spacing={1}
                   sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {user?.has_patient_profile && (
                <Button component={RouterLink} to="/patient"
                        size="small" variant="outlined">
                  My account
                </Button>
              )}
              {user?.has_clinician_profile && (
                <Button component={RouterLink} to="/clinician"
                        size="small" variant="outlined">
                  Clinician
                </Button>
              )}
              {user?.can_administer_practice && (
                <Button component={RouterLink} to="/practice"
                        size="small" variant="outlined">
                  Practice
                </Button>
              )}
            </Stack>
          ) : null}
        </Toolbar>
      </AppBar>

      {/* Mobile nav drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { width: 260 } }}
      >
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" color="primary.main" fontWeight={700}>
            MyBooking Health
          </Typography>
        </Box>
        <List>
          {PUBLIC_LINKS.map((l) => (
            <ListItemButton
              key={l.to}
              component={RouterLink}
              to={l.to}
              onClick={() => setDrawerOpen(false)}
            >
              <ListItemText primary={l.label} />
            </ListItemButton>
          ))}
        </List>
        {status === 'authenticated' && (
          <>
            <Divider sx={{ my: 1 }} />
            <List>
              {user?.has_patient_profile && (
                <ListItemButton component={RouterLink} to="/patient"
                                onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary="My account" />
                </ListItemButton>
              )}
              {user?.has_clinician_profile && (
                <ListItemButton component={RouterLink} to="/clinician"
                                onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary="Clinician" />
                </ListItemButton>
              )}
              {user?.can_administer_practice && (
                <ListItemButton component={RouterLink} to="/practice"
                                onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary="Practice" />
                </ListItemButton>
              )}
            </List>
          </>
        )}
      </Drawer>

      <Container component="main" maxWidth="lg"
                 sx={{ flexGrow: 1, py: { xs: 2, md: 4 },
                       px: { xs: 2, md: 3 } }}>
        <Outlet />
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3, mt: 4,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            MyBooking Medical — infrastructure for trusted care.
            Not a healthcare provider. Always call your local emergency number for life-threatening emergencies.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
